/**
 * TaskService handles all task API interactions.
 * Follows SOLID and DRY principles for maintainability and testability.
 */
import { TASK_API_ERRORS } from '../util/constants';
import { httpClient } from '../util/intercept-fluser';

/**
 * @typedef {Object} Task
 * @property {string} id - Task identifier
 * @property {string} title - Task title
 * @property {string} [comment] - Task detail text
 * @property {string} priority - Task priority (High, Medium, Low)
 * @property {string} assignee - Task assignee
 * @property {string} assignTo - Task target user
 * @property {string} dueDate - Task due date (ISO format)
 * @property {string} redirectUrl - Task redirect URL
 * @property {boolean} completed - Task completion status
 * @property {string} [status] - Task status
 */

/**
 * @typedef {Object} TaskFilters
 * @property {string} [assignee] - Filter by assignee
 * @property {string} [type] - Filter by type
 * @property {string} [status] - Filter by status
 */

/**
 * @typedef {Object} TaskUpdate
 * @property {string} [status] - Updated status
 * @property {Object} [metadata] - Updated metadata
 * @property {string} [deadline] - Updated deadline
 */

/**
 * @typedef {Object} TaskResult
 * @property {boolean} success - Success flag (true)
 * @property {Task|Task[]} data - Response data
 */

/**
 * @typedef {Object} TaskError
 * @property {boolean} success - Success flag (false)
 * @property {boolean} error - Error flag (true)
 * @property {number} status - HTTP status code
 * @property {string} message - Error message
 */

let taskServiceErrorShown = false;

function TaskService(url) {
	this.baseUrl = url;
}

function getStatusCode(error) {
	if (error && typeof error.status === 'number') {
		return error.status;
	}
	const errorMessage = error && typeof error.message === 'string' ? error.message : '';
	const statusMatch = errorMessage.match(/\b([1-5][0-9]{2})\b/);
	if (statusMatch) {
		return parseInt(statusMatch[1], 10);
	}
	return 500;
}

/**
 * Generic fetch wrapper for API calls using httpClient for auth injection.
 * @param {string} endpoint - The API endpoint (appended to baseUrl)
 * @param {Object} [options] - Fetch options
 * @returns {Promise<Object|null>} - Parsed response or null for 204
 * @throws {Error} - Throws on !response.ok with status and statusText
 */
TaskService.prototype.apiRequest = function(endpoint, options) {
	options = options || {};
	var headers = {
		'Accept': 'application/json',
		'Content-Type': 'application/json'
	};
	if (options.headers) {
		for (var key in options.headers) {
			if (Object.prototype.hasOwnProperty.call(options.headers, key)) {
				headers[key] = options.headers[key];
			}
		}
	}

	var requestOptions = Object.assign({}, options, { headers: headers });
	requestOptions.method = requestOptions.method || 'GET';

	return httpClient(`${this.baseUrl}${endpoint}`, requestOptions)
		.then(function(response) {
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			// Handle empty response (e.g., 204 or empty body)
			var contentType = response.headers.get('content-type');
			if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
				return null;
			}
			return response.text().then(function(text) {
				return text ? JSON.parse(text) : null;
			});
		})
		.catch(function(error) {
			if (!taskServiceErrorShown) {
				console.error(TASK_API_ERRORS.API_DOWN);
				taskServiceErrorShown = true;
			}
			throw error;
		});
};

/**
 * Create a new task.
 * @param {Object} taskData - Task data
 * @returns {Promise<Task>} - Created task object
 */
TaskService.prototype.create = function(taskData) {
	return this.apiRequest('/tasks', {
		method: 'POST',
		body: JSON.stringify(taskData)
	});
};

/**
 * Fetch a task by ID.
 * @param {string} taskId - Task identifier
 * @returns {Promise<Task>} - Task object
 */
TaskService.prototype.getById = function(taskId) {
	return this.apiRequest(`/tasks/${taskId}`, { method: 'GET' });
};

/**
 * Fetch a list of tasks with optional filters.
 * @param {TaskFilters} [filters] - Filter options
 * @returns {Promise<Task[]>} - Array of task objects
 */
TaskService.prototype.list = function(filters) {
	filters = filters || {};
	var params = new URLSearchParams();
	if (filters.assignee) {
		params.append('assignee', filters.assignee);
	}
	if (filters.type) {
		params.append('type', filters.type);
	}
	if (filters.status) {
		params.append('status', filters.status);
	}
	var queryString = params.toString();
	var endpoint = '/tasks' + (queryString ? '?' + queryString : '');
	return this.apiRequest(endpoint, { method: 'GET' });
};

/**
 * Update a task.
 * @param {string} taskId - Task identifier
 * @param {TaskUpdate} fields - Fields to update
 * @returns {Promise<Task>} - Updated task object
 */
TaskService.prototype.update = function(taskId, fields) {
	return this.apiRequest(`/tasks/${taskId}`, {
		method: 'PATCH',
		body: JSON.stringify(fields)
	});
};

/**
 * Delete a task.
 * @param {string} taskId - Task identifier
 * @returns {Promise<null>} - No content response
 */
TaskService.prototype.remove = function(taskId) {
	return this.apiRequest(`/tasks/${taskId}`, { method: 'DELETE' });
};

/**
 * Main interface for task operations, exposed globally for mocking/testing.
 * @param {Object} store - Store instance (for state management)
 * @param {string} taskBaseUrl - Base URL for Tasks API
 */
export function taskInterfaceFn(store, taskBaseUrl) {
	var taskService = new TaskService(taskBaseUrl);

	window.TaskServiceInterface = {
		/**
		 * Create a new task.
		 * @param {Object} taskData - Task data to create
		 * @returns {Promise<TaskResult|TaskError>} - Result or error object
		 */
		createTask: function(taskData) {
			return taskService.create(taskData)
				.then(function(createdTask) {
					// Refresh store with updated task list and emit badge count
					var currentTasks = store.get('tasks') || [];
					var updatedTasks = [createdTask, ...currentTasks];
					store.set('tasks', updatedTasks);
					var incompleteCount = updatedTasks.filter(function(t) { return !t.completed; }).length;
					// Emit badge count update
					var events = window.sidekick && window.sidekick.events;
					if (events) {
						events.emit('tasks.unread-count', incompleteCount);
					}
					return { success: true, data: createdTask };
				})
				.catch(function(error) {
					console.error('createTask error:', error);
					return {
						success: false,
						error: true,
						status: getStatusCode(error),
						message: TASK_API_ERRORS.CREATE_FAILED
					};
				});
		},

		/**
		 * Get a task by ID.
		 * @param {string} taskId - Task identifier
		 * @returns {Promise<TaskResult|TaskError>} - Result or error object
		 */
		getTask: function(taskId) {
			if (!taskId) {
				return Promise.resolve({
					success: false,
					error: true,
					status: 400,
					message: TASK_API_ERRORS.INVALID_TASK_ID
				});
			}
			return taskService.getById(taskId)
				.then(function(task) {
					return { success: true, data: task };
				})
				.catch(function(error) {
					console.error('getTask error:', error);
					let statusCode = getStatusCode(error);
					let message = statusCode === 404 ? TASK_API_ERRORS.TASK_NOT_FOUND : TASK_API_ERRORS.FETCH_FAILED;
					return {
						success: false,
						error: true,
						status: statusCode,
						message: message
					};
				});
		},

		/**
		 * Get a list of tasks with optional filters.
		 * @param {TaskFilters} [filters] - Filter options
		 * @returns {Promise<TaskResult|TaskError>} - Result or error object
		 */
		getTasks: function(filters) {
			return taskService.list(filters)
				.then(function(tasks) {
					return { success: true, data: tasks || [] };
				})
				.catch(function(error) {
					console.error('getTasks error:', error);
					let statusCode = getStatusCode(error);
					// Return empty array on 404, error object otherwise
					if (statusCode === 404) {
						return { success: true, data: [] };
					}
					return {
						success: false,
						error: true,
						status: statusCode,
						message: TASK_API_ERRORS.FETCH_FAILED
					};
				});
		},

		/**
		 * Update a task.
		 * @param {string} taskId - Task identifier
		 * @param {TaskUpdate} fields - Fields to update
		 * @returns {Promise<TaskResult|TaskError>} - Result or error object
		 */
		updateTask: function(taskId, fields) {
			if (!taskId) {
				return Promise.resolve({
					success: false,
					error: true,
					status: 400,
					message: TASK_API_ERRORS.INVALID_TASK_ID
				});
			}
			return taskService.update(taskId, fields)
				.then(function(updatedTask) {
					// Handle task completion: decrement badge and call callback
					if (fields.status === 'completed') {
						var currentTasks = store.get('tasks') || [];
						var updatedTasks = currentTasks.map(function(t) {
							return t.id === taskId ? updatedTask : t;
						});
						store.set('tasks', updatedTasks);
						var incompleteCount = updatedTasks.filter(function(t) { return !t.completed; }).length;
						var events = window.sidekick && window.sidekick.events;
						if (events) {
							events.emit('tasks.unread-count', incompleteCount);
						}
						// Call optional onTaskComplete callback if set in store
						var skConfig = store.get('sk-config') || {};
						if (typeof skConfig.onTaskComplete === 'function') {
							skConfig.onTaskComplete(updatedTask);
						}
					}
					return { success: true, data: updatedTask };
				})
				.catch(function(error) {
					console.error('updateTask error:', error);
					let statusCode = getStatusCode(error);
					let message = statusCode === 404 ? TASK_API_ERRORS.TASK_NOT_FOUND : (statusCode === 403 ? TASK_API_ERRORS.PERMISSION_DENIED : TASK_API_ERRORS.UPDATE_FAILED);
					return {
						success: false,
						error: true,
						status: statusCode,
						message: message
					};
				});
		},

		/**
		 * Delete a task.
		 * @param {string} taskId - Task identifier
		 * @returns {Promise<TaskResult|TaskError>} - Result or error object
		 */
		deleteTask: function(taskId) {
			if (!taskId) {
				return Promise.resolve({
					success: false,
					error: true,
					status: 400,
					message: TASK_API_ERRORS.INVALID_TASK_ID
				});
			}
			return taskService.remove(taskId)
				.then(function() {
					// Remove from store and recalculate badge
					var currentTasks = store.get('tasks') || [];
					var updatedTasks = currentTasks.filter(function(t) { return t.id !== taskId; });
					store.set('tasks', updatedTasks);
					var incompleteCount = updatedTasks.filter(function(t) { return !t.completed; }).length;
					var events = window.sidekick && window.sidekick.events;
					if (events) {
						events.emit('tasks.unread-count', incompleteCount);
					}
					return { success: true, data: null };
				})
				.catch(function(error) {
					console.error('deleteTask error:', error);
					let statusCode = getStatusCode(error);
					let message = statusCode === 404 ? TASK_API_ERRORS.TASK_NOT_FOUND : (statusCode === 403 ? TASK_API_ERRORS.PERMISSION_DENIED : TASK_API_ERRORS.DELETE_FAILED);
					return {
						success: false,
						error: true,
						status: statusCode,
						message: message
					};
				});
		}
	};
}

function invokeTaskApiMethod(methodName, args) {
	var taskInterface = window.TaskServiceInterface;
	if (!taskInterface || typeof taskInterface[methodName] !== 'function') {
		return Promise.resolve({
			success: false,
			error: true,
			status: 503,
			message: TASK_API_ERRORS.API_DOWN
		});
	}

	return taskInterface[methodName].apply(taskInterface, args);
}

export const taskApi = {
	getTask: function(taskId) {
		return invokeTaskApiMethod('getTask', [taskId]);
	},
	getTasks: function(filters) {
		return invokeTaskApiMethod('getTasks', [filters]);
	},
	createTask: function(taskData) {
		return invokeTaskApiMethod('createTask', [taskData]);
	},
	updateTask: function(taskId, fields) {
		return invokeTaskApiMethod('updateTask', [taskId, fields]);
	},
	deleteTask: function(taskId) {
		return invokeTaskApiMethod('deleteTask', [taskId]);
	}
};
